import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Modal } from "@mui/material";
import { ThreeDots } from "react-loader-spinner";
import { toast } from "react-toastify";
import API from "../../../api/axios";
import { APP_URLS } from "../../../api/url";
import JSONModal from "../../../assets/SharedComponents/JSONModal/JSONModal";
import { DealerApiWrapper, wideModalStyle } from "../style/DealerApiWrapper";
import { formatDate, humanize, pillClass, toastError } from "../helpers";

/** One push, listing by listing (GET /batches/{batch}: the dealer's view plus each payload). */
const BatchDetailModal = ({ batchId, onClose }) => {
  const open = batchId !== null;
  const [batch, _batch] = useState(null);
  const [items, _items] = useState([]);
  const [loading, _loading] = useState(false);
  const [failed, _failed] = useState(false);
  const [retry, _retry] = useState(0);
  const seq = useRef(0);

  useEffect(() => {
    if (!open) {
      // Invalidate any in-flight request: its response must not repopulate a closed modal.
      seq.current++;
      _batch(null);
      _items([]);
      _failed(false);
      return;
    }

    const current = ++seq.current;
    _loading(true);
    _failed(false);
    API.get(`${APP_URLS.DEALER_API}/batches/${batchId}`)
      .then((res) => {
        if (current !== seq.current) return;
        const resp = res.data;
        if (resp.success === true) {
          _batch(resp.data.batch);
          _items(resp.data.items ?? []);
        } else {
          _failed(true);
          toast.error(resp.message, { position: toast.POSITION.TOP_RIGHT });
        }
      })
      .catch((error) => {
        if (current !== seq.current) return;
        _failed(true);
        toastError(error);
      })
      .finally(() => current === seq.current && _loading(false));
  }, [batchId, open, retry]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={wideModalStyle}>
        <DealerApiWrapper>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0">Push #{batchId}</h4>
            <Button variant="outlined" onClick={onClose} sx={{ textTransform: "none" }}>
              Close
            </Button>
          </div>

          {loading ? (
            <div className="d-flex justify-content-center three-dot">
              <ThreeDots height="100" width="100" radius="9" color="#637df4" visible />
            </div>
          ) : !batch ? (
            // Without this the modal rendered an empty box with a hidden spinner and no way out.
            <div className="text-center">
              <p className="muted">
                {failed ? "This push could not be loaded." : "This push is no longer available — pushes are kept for 90 days."}
              </p>
              {failed && (
                <Button variant="outlined" onClick={() => _retry((n) => n + 1)} sx={{ textTransform: "none" }}>
                  Try again
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="cards">
                <div className="card-stat">
                  <div className="muted">Status</div>
                  <span className={pillClass(batch.status)}>{humanize(batch.status)}</span>
                </div>
                <div className="card-stat">
                  <div className="muted">Dealership</div>
                  <b>{batch.subsidiary?.name ?? `#${batch.subsidiary_id}`}</b>
                </div>
                <div className="card-stat">
                  <div className="muted">Listings</div>
                  <div className="value">{batch.item_count}</div>
                </div>
                <div className="card-stat">
                  <div className="muted">Created / updated / unchanged</div>
                  <div className="value">
                    {batch.created_count} / {batch.updated_count} / {batch.unchanged_count}
                  </div>
                </div>
                <div className={`card-stat${batch.failed_count > 0 ? " danger" : ""}`}>
                  <div className="muted">Failed</div>
                  <div className="value">{batch.failed_count}</div>
                </div>
              </div>
              <p className="muted text-start">
                Accepted {formatDate(batch.accepted_at)} · completed {formatDate(batch.completed_at)} · key #{batch.dealer_api_key_id} ·
                IP {batch.request_ip || "-"}
                {batch.idempotency_key ? ` · Idempotency-Key ${batch.idempotency_key}` : ""}
                {batch.skipped_count > 0 ? ` · ${batch.skipped_count} duplicate reference(s) skipped` : ""}
              </p>

              {items.length === 0 ? (
                <p className="text-start">
                  No per-listing results: they are kept for 30 days after the push (the batch itself for 90 days).
                </p>
              ) : (
                <div className="tableContainer">
                  <table className="auctionTable">
                    <thead className="tableHead">
                      <tr className="table-row">
                        <th className="time-start">#</th>
                        <th className="time-start">Reference</th>
                        <th className="time-start">Status</th>
                        <th className="time-start">Error / warnings</th>
                        <th className="time-start">Ad</th>
                        <th className="time-start">Photos</th>
                        <th className="time-start">Payload</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => (
                        <tr key={`item-${item.receipt_id}`} className="table-row">
                          <td>{item.index ?? "-"}</td>
                          <td className="mono">{item.external_reference}</td>
                          <td>
                            <span className={pillClass(item.status)}>{humanize(item.status)}</span>
                            <div className="muted">{formatDate(item.processed_at)}</div>
                          </td>
                          <td className="text-start">
                            {item.error ? (
                              <>
                                <b className="mono">{item.error.code}</b>
                                <div className="muted">{item.error.message}</div>
                                {item.error.details && (
                                  <JSONModal buttonText="Details" modalHeader={`Error ${item.error.code}`} jsonData={item.error.details} />
                                )}
                              </>
                            ) : null}
                            {(item.warnings ?? []).map((w, i) => (
                              <div key={`w-${i}`} className="muted">
                                ⚠ <span className="mono">{w.code}</span>
                              </div>
                            ))}
                            {!item.error && !(item.warnings ?? []).length ? "-" : null}
                          </td>
                          <td>{item.ad_id ?? "-"}</td>
                          <td>
                            {item.images ? `${item.images.attached} attached, ${item.images.failed} failed` : "-"}
                            {item.images?.results?.length > 0 && (
                              <JSONModal buttonText="Photo results" modalHeader="Per-photo results" jsonData={item.images.results} />
                            )}
                          </td>
                          <td>
                            {item.payload ? <JSONModal buttonText="Payload" modalHeader={item.external_reference} jsonData={item.payload} /> : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </DealerApiWrapper>
      </Box>
    </Modal>
  );
};

export default BatchDetailModal;
